import type { LogLevel } from './sinks.js';

const SEVERITY: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 };

export function isLogLevel(value: unknown): value is LogLevel {
  return typeof value === 'string' && value in SEVERITY;
}

type LevelEnv = { LOG_LEVEL?: unknown; NODE_ENV?: unknown };

/**
 * The minimum level when nothing calls {@link setMinLogLevel}: a valid
 * `LOG_LEVEL`, else `debug` when `NODE_ENV` is set and not `production`, else
 * `info`. A browser bundle has no `process`, so it gets `info` unless the app
 * sets the level at startup.
 *
 * @internal
 */
export function defaultMinLogLevel(env: LevelEnv | undefined): LogLevel {
  if (isLogLevel(env?.LOG_LEVEL)) {
    return env.LOG_LEVEL;
  }
  if (env?.NODE_ENV === undefined || env.NODE_ENV === 'production') {
    return 'info';
  }
  return 'debug';
}

/** `process.env` in Node. Packages that typecheck this source without `@types/node` see no `process`. */
function readProcessEnv(): LevelEnv | undefined {
  const runtime: { globalThis: unknown; process?: unknown } = globalThis;
  const nodeProcess = runtime.process;
  if (typeof nodeProcess !== 'object' || nodeProcess === null || !('env' in nodeProcess)) {
    return undefined;
  }
  const { env } = nodeProcess;
  return typeof env === 'object' && env !== null ? env : undefined;
}

let minLevel: LogLevel = defaultMinLogLevel(readProcessEnv());

/** Drop every later entry below `level`, before the console write and before the sinks. */
export function setMinLogLevel(level: LogLevel): void {
  minLevel = level;
}

/** @internal */
export function getMinLogLevel(): LogLevel {
  return minLevel;
}

export function isLogLevelEnabled(level: LogLevel): boolean {
  return SEVERITY[level] >= SEVERITY[minLevel];
}
